import cx from "classnames";
import { graphql } from "gatsby";
import React from "react";
import { useState } from "react";
import { useClientSide } from "../../../hooks/useClientSide";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";
import { ParagraphTextOrButton } from "../ParagraphTextOrButton/ParagraphTextOrButton";
import * as styles from "./ParagraphContentSection.module.scss";

interface ParagraphContentSectionProps {
	heading?: string;
	preHeading?: string;
	textOrButtons?: Queries.ParagraphTextOrButtonDataFragment[];
	isCentred?: boolean;
	image?: DogsTrust.MediaImageProps;
	theme: "primary" | "secondary";
	jumpLinksTitle?: string;
}
export const getParagraphContentSectionProps = (
	{
		field_pre_heading,
		field_heading,
		field_jump_links_title,
		field_theme,
		relationships: { field_image, field_text_button },
	}:
		| Queries.ParagraphContentSectionDataFragment
		| Queries.ParagraphSectionsData_paragraph__centre_aligned_content_section_Fragment,
	isCentred?: boolean,
): ParagraphContentSectionProps => ({
	heading: field_heading,
	jumpLinksTitle: field_jump_links_title,
	preHeading: field_pre_heading,
	textOrButtons:
		field_text_button as Queries.ParagraphTextOrButtonDataFragment[],
	isCentred,
	image: field_image?.[0]
		? {
				src: field_image?.[0]?.field_media_image.uri,
				alt: field_image?.[0]?.field_media_image.alt,
			}
		: undefined,
	theme: !field_theme ? "primary" : (field_theme as "primary" | "secondary"),
});
export const ParagraphContentSection: React.FC<
	ParagraphContentSectionProps
> = ({
	heading,
	preHeading,
	textOrButtons,
	isCentred = false,
	image,
	theme = "primary",
	jumpLinksTitle,
}) => {
	const [calculatedPrimaryColour, setCalculatedPrimaryColour] =
		useState<string>(null);

	const isClientSide = useClientSide();
	return (
		<WrappedSection
			sectionName="ParagraphContentSection"
			sectionColour={
				theme === "primary" ? Colours.SUNSHINE_YELLOW : Colours.SANDY_FUR
			}
			sectionTopImage={image}
			updateParagraphContentSectionTheme={setCalculatedPrimaryColour}
		>
			{!isClientSide && <div className={styles.imagelayoutshiftblock} />}
			<div
				className={cx(styles.contentarea, {
					[styles.primary]: theme === "primary",
				})}
				style={{
					alignItems: isCentred ? "center" : "flex-start",
					textAlign: isCentred ? "center" : "left",
				}}
			>
				{preHeading?.length > 0 && <p>{preHeading}</p>}
				{heading?.length > 0 &&
					(jumpLinksTitle ? (
						<h2 data-jumplinkstitle={jumpLinksTitle} className={styles.heading}>
							{heading}
						</h2>
					) : (
						<h2 className={styles.heading}>{heading}</h2>
					))}
				{textOrButtons?.map((textOrButton) => (
					<ParagraphTextOrButton
						key={textOrButton.id}
						data={textOrButton}
						parentTheme={calculatedPrimaryColour}
					/>
				))}
			</div>
		</WrappedSection>
	);
};

export const fragment = graphql`
    fragment ParagraphContentSectionUnionData on ParagraphContentSectionUnion {
        __typename
        ...ParagraphContentSectionData
        ...ParagraphCentreAlignedContentSectionData
    }

    fragment ParagraphContentSectionData on paragraph__content_section {
        __typename
        id
        field_pre_heading
        field_heading
		field_jump_links_title
        field_theme
        relationships {
            __typename
            field_image {
                field_media_image {
                    alt
                    uri
                }
            }
            field_text_button {
                ...ParagraphTextOrButtonData
            }
        }
    }
    fragment ParagraphCentreAlignedContentSectionData on paragraph__centre_aligned_content_section {
        __typename
        id
        field_pre_heading
        field_heading
        field_theme
        relationships {
            __typename
            field_image {
                field_media_image {
                    alt
                    uri
                }
            }
            field_text_button {
                ...ParagraphTextOrButtonData
            }
        }
    }
`;
